"use client";

import type { JSX } from "react";
import { Icon, Link, Text, View } from "@/components";
import { useLayout } from "@/providers";
import cn from "@/utils/cn";

export interface StudentSessionBarProps {
  /** Clases extra para ajustar la posición desde el layout. */
  className?: string;
}

/**
 * @name StudentSessionBar
 * @description Cierre de sesión de la superficie del alumno (/elearning/mi-aprendizaje).
 * El Nav institucional global solo muestra el menú de perfil (con su logout)
 * cuando hay `userAuth` en sessionStorage, que este proyecto no setea; y el
 * alumno tampoco pasa por el sidebar del backoffice, así que este link es su
 * única salida.
 *
 * Desde `sm` se superpone (fixed) sobre la franja libre del Nav global — entre
 * los logos y el switcher de tema — para no quitarle espacio vertical al
 * contenido. Por debajo de `sm` NO hay franja libre: los logos del Nav (de ancho
 * fijo) llegan hasta donde empezaría el link, así que ahí va en el flujo, arriba
 * a la derecha del contenido, con su texto y con área táctil completa.
 *
 * @param {StudentSessionBarProps} props - Clases extra opcionales.
 * @returns {JSX.Element} El link de cierre de sesión.
 */
const StudentSessionBar = ({
  className
}: StudentSessionBarProps): JSX.Element => {
  const { t } = useLayout();

  return (
    <View
      className={cn(
        // Mobile: en el flujo del contenido (el Nav no tiene lugar libre).
        "flex justify-end w-full",
        // sm+: superpuesto al Nav global (fixed z-[105], min-h-[84px]); right-20
        // deja libre el switcher de tema (~48px + padding del Nav).
        // El pb-1.5 no es aire: los 84px del Nav son border-box e incluyen su
        // borde inferior de 6px, así que el Nav centra a sus hijos en una caja
        // de contenido de 78px. Sin descontar esos 6px acá, `items-center`
        // centraría en 84 y el link quedaría 3px más abajo que el switcher de
        // tema. Medido en el navegador.
        "sm:fixed sm:top-0 sm:right-20 sm:z-106 sm:h-21 sm:pb-1.5 sm:w-auto sm:items-center",
        className
      )}
    >
      <Link
        replace
        href="/signOut"
        aria-label={t("signOut")}
        // min-h-11 + px-2: área táctil de 44px en mobile; en sm+ vuelve al link
        // de texto del Nav, sin caja propia.
        className="inline-flex items-center gap-2 min-h-11 px-2 sm:min-h-0 sm:px-0 text-sm font-medium text-navbar-foreground/80 hover:text-navbar-foreground hover:underline transition-colors"
      >
        {/* El átomo Icon ya antepone `fa-solid fa-`: el nombre va sin prefijo. */}
        <Icon name="arrow-right-from-bracket" size="3x" aria-hidden="true" />
        <Text type="span">{t("signOut")}</Text>
      </Link>
    </View>
  );
};

export default StudentSessionBar;
